import { createApi } from '@reduxjs/toolkit/query/react';
import { Api } from 'api';
import { URL } from 'api/ApiUrls';

export const MembershipApi = createApi({
    reducerPath: 'MembershipApi',
    baseQuery: Api.fetchBaseQuery,
    tagTypes: ['Membership', 'STAGE_RESULT', 'RENEWAL', 'INTERVIEW_SCHEDULE', 'ISO_CERT', 'CERT_DOC'],
    endpoints: (build) => ({
        getMembership: build.query({
            query: (params) => `${URL.MEMBERSHIP_LIST}?${params}`,
            providesTags: ['LIST']
        }),
        getMembershipDetails: build.query({
            query: (params) => `${URL.MEMBERSHIP_LIST}/${params}`,
            providesTags: ['DETAIL']
        }),
        getMemberCertRates: build.query({
            query: (params) => `${URL.MEMBERSHIP_CERT_RATES}/${params}`,
            providesTags: ['USER_CERT_RATES']
        }),
        getMemberCompanyMemberList: build.query({
            query: (params) => `${URL.MEMBERSHIP_COMPANY_MEMBER_LIST}/${params}`,
            providesTags: ['USER_COMPANY_MEMBER_LIST']
        }),
        updateMemberCompanyMember: build.mutation({
            query: (body) => ({
                url: `${URL.MEMBERSHIP_COMPANY_MEMBER_APPROVE_YN}`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['USER_COMPANY_MEMBER_LIST']
        }),

        createMembership: build.mutation({
            query: (body) => ({
                url: `${URL.MEMBERSHIP_CREATE}`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['LIST', 'DETAIL']
        }),
        updateMembership: build.mutation({
            query: (body) => ({
                url: `${URL.MEMBERSHIP_UPDATE}`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['LIST', 'DETAIL']
        }),
        updateMemberPaymentComplete: build.mutation({
            query: (body) => ({
                url: `${URL.MEMBERSHIP_PAYMENT_COMPLETE}`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['LIST', 'DETAIL']
        }),
        updateUserIdentified: build.mutation({
            query: (body) => ({
                url: `${URL.MEMBERSHIP_IDENTIFIED_NUM_CREATE}`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['LIST', 'DETAIL']
        }),
        deleteUserIdentified: build.mutation({
            query: (body) => ({
                url: `${URL.MEMBERSHIP_IDENTIFIED_NUM_DELETE}`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['LIST', 'DETAIL']
        }),

        updateMemberCertRate: build.mutation({
            query: (body) => ({
                url: `${URL.MEMBERSHIP_CERT_RATE_UPDATE}`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['USER_CERT_RATES']
        }),
        // 2단계 평가 항목별 현장 확인(Y/N)
        updateSiteCheck: build.mutation({
            query: (body) => ({
                url: `${URL.MEMBERSHIP_CERT_RATE_SITE_CHECK}`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['USER_CERT_RATES']
        }),
        deleteMembership: build.mutation({
            query: (body) => ({
                url: `${URL.MEMBERSHIP_DELETE}/${body}`,
                method: 'POST',
                body: body
            }),
            invalidatesTags: ['LIST', 'DETAIL']
        }),
        deleteMemberships: build.mutation({
            query: (body) => ({
                url: `${URL.MEMBERSHIPS_DELETE}`,
                method: 'DELETE',
                body: body
            }),
            invalidatesTags: ['LIST']
        }),

        // Financial
        getMembershipByYear: build.query({
            query: (params) => `${URL.FINANCIAL_LIST_BY_YEAR}?${params}`,
            providesTags: ['DETAIL']
        }),
        // 재무자료를 KODATA에서 받아온 이력. 평가 시 숫자의 출처를 확인하는 용도.
        getKodataFinancial: build.query({
            query: (saupNum) => `${URL.KODATA_FINANCIAL}/${saupNum}`,
            providesTags: ['DETAIL']
        }),
        // 사용자가 1단계 평가 화면에서 불러온 기술인증·특허 정보. 보관분만 조회하며 과금이 없다.
        getKodataCert: build.query({
            query: (saupNum) => `${URL.KODATA_CERT}/${saupNum}`,
            providesTags: ['DETAIL']
        }),
        // 1단계 판정(대상/비대상)과 2단계 진입 가능 여부
        // 1단계 인증·특허 증빙(업체가 직접 올린 파일). 관리자는 확인·내려받기만 한다.
        getCertDoc: build.query({
            query: (userSeq) => `${URL.CERT_DOC}/${userSeq}`,
            providesTags: ['CERT_DOC']
        }),
        // 2단계 ISO 인증현황(관리자 입력). 저장은 화면의 행 전체로 교체한다.
        getIsoCerts: build.query({
            query: (userSeq) => `${URL.ISO_CERT}/${userSeq}`,
            providesTags: ['ISO_CERT']
        }),
        saveIsoCerts: build.mutation({
            query: (body) => ({
                url: `${URL.ISO_CERT_BULK}`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['ISO_CERT']
        }),
        // 2단계 현장 인터뷰평가 희망 일정(업체 제출)과 확정
        getInterviewSchedule: build.query({
            query: (userSeq) => `${URL.INTERVIEW_SCHEDULE}/${userSeq}`,
            providesTags: ['INTERVIEW_SCHEDULE']
        }),
        confirmInterviewSchedule: build.mutation({
            query: (body) => ({
                url: `${URL.INTERVIEW_SCHEDULE_CONFIRM}`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['INTERVIEW_SCHEDULE']
        }),
        getStageResult: build.query({
            query: (userSeq) => `${URL.STAGE_RESULT}/${userSeq}`,
            providesTags: ['STAGE_RESULT']
        }),
        updateStageResult: build.mutation({
            query: (body) => ({
                url: `${URL.STAGE_RESULT}`,
                method: 'POST',
                body
            }),
            // 2단계 인정 저장 시 식별번호가 발급되므로 기업정보(DETAIL)와 목록도 다시 불러온다.
            invalidatesTags: ['STAGE_RESULT', 'DETAIL', 'LIST']
        }),
        // 인증 갱신(갱신 수수료는 최초 평가 결제와 따로 관리한다)
        getRenewal: build.query({
            query: (userSeq) => `${URL.RENEWAL_DETAIL}/${userSeq}`,
            providesTags: ['RENEWAL']
        }),
        // 유효기간 안내 메일 수동 발송(자동 발송과 같은 문구)
        sendRenewalNotice: build.mutation({
            query: (body) => ({
                url: `${URL.RENEWAL_NOTICE_SEND}`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['RENEWAL']
        }),
        updateRenewalPaymentComplete: build.mutation({
            query: (body) => ({
                url: `${URL.RENEWAL_PAYMENT_COMPLETE}`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['RENEWAL', 'LIST', 'DETAIL']
        }),

        createFinancial: build.mutation({
            query: (body) => ({
                url: `${URL.FINANCIAL_CREATE}`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['DETAIL']
        }),
        updateFinancial: build.mutation({
            query: (body) => ({
                url: `${URL.FINANCIAL_UPDATE}`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['DETAIL']
        }),

        // Self
        getSelfDiagnosis: build.query({
            query: (params) => `${URL.SELF_DIAGNOSIS}/${params}`,
            providesTags: ['SELF_LIST']
        }),
        createSelfDiagnosis: build.mutation({
            query: (body) => ({
                url: `${URL.SELF_DIAGNOSIS_CREATE}`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['SELF_LIST']
        }),
        updateSelfDiagnosis: build.mutation({
            query: (body) => ({
                url: `${URL.SELF_DIAGNOSIS_UPDATE}`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['SELF_LIST']
        }),
        evaluationCompleteEmail: build.mutation({
            query: (body) => ({
                url: `${URL.MEMBERSHIP_EVALUATION_EMAIL_COMPLETE}`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['LIST', 'DETAIL']
        }),
        eligibilityCompleteEmail: build.mutation({
            query: (body) => ({
                url: `${URL.MEMBERSHIP_ELIGIBILITY_EMAIL_COMPLETE}`,
                method: 'POST',
                body
            }),
            invalidatesTags: ['LIST', 'DETAIL']
        }),
    })
});

export const {
    useGetMembershipQuery,
    // 엑셀 다운로드: 버튼을 눌렀을 때만 검색 결과 전체를 불러온다.
    useLazyGetMembershipQuery,
    useGetMembershipByYearQuery,
    useGetKodataFinancialQuery,
    useGetKodataCertQuery,
    useGetStageResultQuery,
    useGetInterviewScheduleQuery,
    useGetIsoCertsQuery,
    useGetCertDocQuery,
    useSaveIsoCertsMutation,
    useConfirmInterviewScheduleMutation,
    useUpdateStageResultMutation,
    useGetRenewalQuery,
    useUpdateRenewalPaymentCompleteMutation,
    useSendRenewalNoticeMutation,
    useGetMembershipDetailsQuery,
    useGetMemberCertRatesQuery,
    useCreateMembershipMutation,
    useUpdateMembershipMutation,
    useUpdateMemberCertRateMutation,
    useUpdateSiteCheckMutation,
    useDeleteMembershipMutation,
    useDeleteMembershipsMutation,

    useUpdateMemberPaymentCompleteMutation,
    useUpdateUserIdentifiedMutation,
    useDeleteUserIdentifiedMutation,

    useGetMemberCompanyMemberListQuery,
    useUpdateMemberCompanyMemberMutation,

    useCreateFinancialMutation,
    useUpdateFinancialMutation,

    useGetSelfDiagnosisQuery,
    useCreateSelfDiagnosisMutation,
    useUpdateSelfDiagnosisMutation,

    useEvaluationCompleteEmailMutation,
    useEligibilityCompleteEmailMutation

} = MembershipApi;
